import React, { useState } from 'react';
import './Schedule.css';
import studentsBanner from '../novas-students-banner-template.jpeg';
import AddLessonModal from '../components/Schedule/AddLessonModal';
import EditLessonModal from '../components/Schedule/EditLessonModal';
import RecurringEditModal from '../components/Schedule/RecurringEditModal';
import DeleteRecurringModal from '../components/Schedule/DeleteRecurringModal';
import { useScheduleData } from '../hooks/useScheduleData';
import { useScheduleCalendar } from '../hooks/useScheduleCalendar';
import { useScheduleLessonActions } from '../hooks/useScheduleLessonActions';

const Schedule = () => {
  const {
    currentDate, setCurrentDate,
    selectedDate, setSelectedDate,
    viewMode, setViewMode,
    lessons, setLessons,
    isLoading,
    students, groups, isLoadingStudentsGroups,
  } = useScheduleData();

  const [selectedFilter, setSelectedFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [addLessonSaving, setAddLessonSaving] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalTime, setModalTime] = useState('09:00');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isSaving, setIsSaving] = useState(false);
  const [recurringEditModalOpen, setRecurringEditModalOpen] = useState(false);
  const [pendingLessonUpdate, setPendingLessonUpdate] = useState(null);
  const [deleteRecurringModalOpen, setDeleteRecurringModalOpen] = useState(false);
  const [pendingLessonDelete, setPendingLessonDelete] = useState(null);
  const [updateLessonSaving, setUpdateLessonSaving] = useState(false);
  const [deleteLessonSaving, setDeleteLessonSaving] = useState(false);
  const [deleteRecurringSaving, setDeleteRecurringSaving] = useState(false);

  const {
    getLessonsForDate,
    getCalendarData,
    generateTimeSlots,
    handlePrevPeriod,
    handleNextPeriod,
    getStudentName,
    formatTime,
    getStudentNameWithTime,
    dayNames,
    getCalendarHeader,
  } = useScheduleCalendar({
    lessons, selectedFilter,
    currentDate, setCurrentDate,
    viewMode,
    students, groups, isLoadingStudentsGroups,
    setSelectedDate,
  });

  const {
    handleAddLesson,
    handleSaveLesson,
    handleEditLesson,
    handleUpdateLesson,
    updateLesson,
    handleDeleteLesson,
    handleDeleteClick,
    handleDeleteRecurringThis,
    handleDeleteRecurringFollowing,
  } = useScheduleLessonActions({
    modalDate, setModalDate, setModalOpen, setAddLessonSaving,
    getStudentName, groups, students,
    lessons, setLessons,
    selectedLesson, setSelectedLesson, setEditModalOpen,
    setPendingLessonUpdate, setRecurringEditModalOpen, setUpdateLessonSaving,
    setDeleteLessonSaving,
    pendingLessonDelete, setPendingLessonDelete, setDeleteRecurringModalOpen, setDeleteRecurringSaving,
  });

  return (
    <div className="schedule-container">
      <div className="schedule-header">
        <h1>Schedule {isSaving && <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>• Saving...</span>}</h1>
        
        <div className="schedule-controls">
          <div className="view-mode-selector">
            <button 
              className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button 
              className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
            <button 
              className={`view-btn ${viewMode === 'day' ? 'active' : ''}`}
              onClick={() => setViewMode('day')}
            >
              Day
            </button>
          </div>

          <div className="student-filter">
            <select 
              value={selectedFilter} 
              onChange={(e) => setSelectedFilter(e.target.value)}
            >
              <option value="all">All Students & Groups</option>
              <optgroup label="Groups">
                {Object.entries(groups).map(([id, g]) => (
                  <option key={`group-${id}`} value={`group-${id}`}>● {g.name}</option>
                ))}
              </optgroup>
              <optgroup label="Students">
                {Object.entries(students).map(([id, s]) => (
                  <option key={`student-${id}`} value={`student-${id}`}>○ {s.name}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="schedule-loading">
          <span className="schedule-loading-spinner" aria-hidden />
          <p className="schedule-loading-text">Loading schedule...</p>
        </div>
      ) : (
      <>
      <div className="calendar-container">
        <div 
          className="calendar-header custom-calendar-header"
          style={{
            backgroundImage: `url(${studentsBanner})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            zIndex: 1,
            height: '130px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }}
        >
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(40,40,40,0.55)',
            zIndex: 2
          }} />
          <button 
            className="schedule-nav-btn schedule-prev" 
            onClick={() => {
              console.log('Left arrow clicked!');
              handlePrevPeriod();
            }}
            style={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10, pointerEvents: 'auto' }}
          >
            ←
          </button>
          <h2 style={{
            margin: 0,
            fontSize: 32,
            fontWeight: 700,
            color: 'white',
            position: 'relative',
            zIndex: 3,
            textAlign: 'center',
            width: '100%'
          }}>{getCalendarHeader()}</h2>
          <button 
            className="schedule-nav-btn schedule-next" 
            onClick={handleNextPeriod}
            style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10, pointerEvents: 'auto' }}
          >
            →
          </button>
        </div>

        <div className="calendar">
          <div className="calendar-weekdays">
            {dayNames.map((day, i) => {
              const today = new Date();
              const todayIndex = (today.getDay() + 6) % 7;
              const isToday = i === todayIndex;
              let dateLabel = '';
              if (viewMode === 'week') {
                const weekData = getCalendarData()[0];
                const dayObj = weekData[i];
                if (dayObj && dayObj.date) {
                  dateLabel = dayObj.date.getDate();
                }
              }
              let showDate = false;
              if (viewMode === 'week' && dateLabel) showDate = true;
              if (viewMode === 'day') {
                const currentIndex = (currentDate.getDay() + 6) % 7;
                if (i === currentIndex) {
                  dateLabel = currentDate.getDate();
                  showDate = true;
                } else {
                  showDate = false;
                }
              }
              return (
                <div key={day} className={`weekday-header${isToday ? ' today-header' : ''}`}>
                  {day}
                  {showDate && (
                    <div className="weekday-date">{dateLabel}</div>
                  )}
                </div>
              );
            })}
          </div>
          {viewMode === 'month' ? (
            <>
              <AddLessonModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveLesson}
                date={modalDate || new Date()}
                students={students}
                groups={groups}
                initialTime={modalTime}
                saving={addLessonSaving}
              />
              <RecurringEditModal
                open={recurringEditModalOpen}
                onClose={() => {
                  setRecurringEditModalOpen(false);
                  setPendingLessonUpdate(null);
                  setEditModalOpen(true);
                }}
                onEditThis={() => {
                  setUpdateLessonSaving(true);
                  updateLesson(pendingLessonUpdate, 'single');
                }}
                onEditFollowing={() => {
                  setUpdateLessonSaving(true);
                  updateLesson(pendingLessonUpdate, 'following');
                }}
                lesson={pendingLessonUpdate}
              />
              <EditLessonModal
                open={editModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setSelectedLesson(null);
                  setUpdateLessonSaving(false);
                  setDeleteLessonSaving(false);
                }}
                onSave={handleUpdateLesson}
                onDelete={handleDeleteLesson}
                lesson={selectedLesson}
                students={students}
                groups={groups}
                onDeleteClick={handleDeleteClick}
                updateSaving={updateLessonSaving}
                deleteSaving={deleteLessonSaving}
              />
              <DeleteRecurringModal
                open={deleteRecurringModalOpen}
                saving={deleteRecurringSaving}
                onClose={() => {
                  if (deleteRecurringSaving) return;
                  setDeleteRecurringModalOpen(false);
                  setPendingLessonDelete(null);
                  setEditModalOpen(true);
                }}
                onDeleteThis={handleDeleteRecurringThis}
                onDeleteFollowing={handleDeleteRecurringFollowing}
                lesson={pendingLessonDelete}
              />
              <div className="calendar-grid">
                {getCalendarData().map((week, weekIndex) => (
                  <div key={weekIndex} className="calendar-week">
                    {week.map((day, dayIndex) => (
                      <div
                        key={dayIndex}
                        className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${selectedDate && day.date && day.date.toDateString() === selectedDate.toDateString() ? 'selected' : ''} ${!day.date ? 'empty-day' : ''}`}
                        onClick={() => day.date && handleAddLesson(day.date)}
                      >
                        <div className="day-number">{day.date?.getDate()}</div>
                        <div className="day-lessons">
                          {(() => {
                            const regularLessons = day.lessons
                              .filter(lesson => !lesson.allDay)
                              .sort((a, b) => {
                                // Сортируем по времени начала
                                const timeA = a.from || '00:00';
                                const timeB = b.from || '00:00';
                                return timeA.localeCompare(timeB);
                              });
                            const allDayLessons = day.lessons.filter(lesson => lesson.allDay);
                            const maxRegularToShow = 3; // Показываем 3 обычных мероприятия
                            
                            // Отладочная информация
                            console.log(`Day ${day.date?.getDate()}:`, {
                              total: day.lessons.length,
                              regular: regularLessons.length,
                              allDay: allDayLessons.length,
                              shouldShowMore: regularLessons.length > maxRegularToShow
                            });
                            
                            return (
                              <>
                                {regularLessons.slice(0, maxRegularToShow).map((lesson, index) => (
                                  <div
                                    key={index}
                                    className={`lesson-indicator ${lesson.status || 'in_progress'} ${lesson.type === 'event' ? 'event' : ''}`}
                                    title={`${lesson.type === 'lesson' ? getStudentName(lesson.studentId, lesson.groupId) + ' - ' : ''}${lesson.title || 'Untitled Item'}`}
                                    style={{ position: 'relative', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                    onClick={e => { e.stopPropagation(); handleEditLesson(lesson); }}
                                  >
                                    {getStudentNameWithTime(lesson)}
                                  </div>
                                ))}
                                {allDayLessons.map((lesson, index) => (
                                  <div
                                    key={`allday-${index}`}
                                    className={`lesson-indicator ${lesson.status || 'in_progress'} ${lesson.type === 'event' ? 'event' : ''} all-day`}
                                    title={`${lesson.type === 'lesson' ? getStudentName(lesson.studentId, lesson.groupId) + ' - ' : ''}${lesson.title || 'Untitled Item'} (All Day)`}
                                    style={{ 
                                      position: 'relative', 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      cursor: 'pointer',
                                      fontWeight: 'bold'
                                    }}
                                    onClick={e => { e.stopPropagation(); handleEditLesson(lesson); }}
                                  >
                                    {getStudentNameWithTime(lesson)}
                                  </div>
                                ))}
                                {regularLessons.length > maxRegularToShow && (
                                  <div 
                                    className="more-lessons"
                                    onClick={e => { 
                                      e.stopPropagation(); 
                                      setSelectedDate(day.date);
                                      // Автоматическая прокрутка вниз к списку уроков
                                      setTimeout(() => {
                                        const detailsSection = document.querySelector('.selected-date-details');
                                        if (detailsSection) {
                                          detailsSection.scrollIntoView({ 
                                            behavior: 'smooth', 
                                            block: 'start' 
                                          });
                                        }
                                      }, 100);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                    title={`Click to see all ${day.lessons.length} events`}
                                  >
                                    +{regularLessons.length - maxRegularToShow}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <AddLessonModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveLesson}
                date={modalDate || new Date()}
                students={students}
                groups={groups}
                initialTime={modalTime}
                saving={addLessonSaving}
              />
              <RecurringEditModal
                open={recurringEditModalOpen}
                onClose={() => {
                  setRecurringEditModalOpen(false);
                  setPendingLessonUpdate(null);
                  setEditModalOpen(true);
                }}
                onEditThis={() => {
                  setUpdateLessonSaving(true);
                  updateLesson(pendingLessonUpdate, 'single');
                }}
                onEditFollowing={() => {
                  setUpdateLessonSaving(true);
                  updateLesson(pendingLessonUpdate, 'following');
                }}
                lesson={pendingLessonUpdate}
              />
              <EditLessonModal
                open={editModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setSelectedLesson(null);
                  setUpdateLessonSaving(false);
                  setDeleteLessonSaving(false);
                }}
                onSave={handleUpdateLesson}
                onDelete={handleDeleteLesson}
                lesson={selectedLesson}
                students={students}
                groups={groups}
                onDeleteClick={handleDeleteClick}
                updateSaving={updateLessonSaving}
                deleteSaving={deleteLessonSaving}
              />
              <DeleteRecurringModal
                open={deleteRecurringModalOpen}
                saving={deleteRecurringSaving}
                onClose={() => {
                  if (deleteRecurringSaving) return;
                  setDeleteRecurringModalOpen(false);
                  setPendingLessonDelete(null);
                  setEditModalOpen(true);
                }}
                onDeleteThis={handleDeleteRecurringThis}
                onDeleteFollowing={handleDeleteRecurringFollowing}
                lesson={pendingLessonDelete}
              />
              <div className="time-grid">
                <div className="time-labels">
                  <div className="time-label empty"></div>
                  {generateTimeSlots().map((slot, index) => (
                    <div key={index} className="time-label">{slot.time}</div>
                  ))}
                </div>
                <div className={viewMode === 'week' ? 'week-grid' : 'day-grid'}>
                  {/* All Day Events Section - First Row */}
                  {viewMode === 'week' ? (
                    // Week view: all day events in first row
                    (() => {
                      const weekData = getCalendarData()[0];
                      return weekData.map((day, dayIndex) => {
                        const allDayEvents = day.lessons.filter(lesson => lesson.allDay);
                        return (
                          <div key={`allday-${dayIndex}`} className="time-cell all-day-cell">
                            {allDayEvents.map((event, eventIndex) => (
                              <div
                                key={eventIndex}
                                className={`all-day-event ${event.type === 'lesson' ? 'lesson' : ''} ${event.status || 'in_progress'}`}
                                title={`${event.type === 'lesson' ? getStudentName(event.studentId, event.groupId) + ' - ' : ''}${event.title || 'Untitled Item'}`}
                                onClick={e => { e.stopPropagation(); handleEditLesson(event); }}
                              >
                                {getStudentNameWithTime(event)}
                              </div>
                            ))}
                          </div>
                        );
                      });
                    })()
                  ) : (
                    // Day view: all day events in first cell
                    (() => {
                      const weekData = getCalendarData()[0];
                      const dayIndex = (currentDate.getDay() + 6) % 7;
                      const dayData = weekData[dayIndex];
                      const allDayEvents = dayData.lessons.filter(lesson => lesson.allDay);
                      return (
                        <div className="time-cell all-day-cell">
                          {allDayEvents.map((event, eventIndex) => (
                            <div
                              key={eventIndex}
                              className={`all-day-event ${event.type === 'lesson' ? 'lesson' : ''} ${event.status || 'in_progress'}`}
                              title={`${event.type === 'lesson' ? getStudentName(event.studentId, event.groupId) + ' - ' : ''}${event.title || 'Untitled Item'}`}
                              onClick={e => { e.stopPropagation(); handleEditLesson(event); }}
                            >
                              {getStudentNameWithTime(event)}
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  )}
                  {generateTimeSlots().map((slot, timeIndex) => (
                    viewMode === 'week' ? (
                      Array.from({ length: 7 }, (_, dayIndex) => {
                        const weekData = getCalendarData()[0];
                        const day = weekData[dayIndex];
                        const lessonsForThisTime = day.lessons.filter(lesson => {
                          if (!lesson.from || !lesson.to || lesson.allDay) return false;
                          
                          const fromTime = formatTime(lesson.from || lesson.timeFrom || '9:00');
                          const startHour = parseInt(fromTime.split(':')[0]);
                          
                          // Событие отображается только в ячейке начала
                          return slot.hour === startHour;
                        });
                        return (
                          <div 
                            key={`${timeIndex}-${dayIndex}`} 
                            className="time-cell"
                            onClick={() => {
                              if (day.date) {
                                const timeString = `${slot.hour.toString().padStart(2, '0')}:00`;
                                setModalTime(timeString);
                                setModalDate(day.date);
                                setModalOpen(true);
                              }
                            }}
                          >
                            {lessonsForThisTime.map((lesson, lessonIndex) => {
                              const fromTime = formatTime(lesson.from || lesson.timeFrom || '9:00');
                              const toTime = formatTime(lesson.to || lesson.timeTo || '10:00');
                              const startHour = parseInt(fromTime.split(':')[0]);
                              const startMinute = parseInt(fromTime.split(':')[1]) || 0;
                              const endHour = parseInt(toTime.split(':')[0]);
                              const endMinute = parseInt(toTime.split(':')[1]) || 0;
                              
                              const startTime = startHour + startMinute / 60;
                              const endTime = endHour + endMinute / 60;
                              const duration = endTime - startTime;
                              const height = Math.max(duration * 21, 21); // 24px на час
                              
                              // Вычисляем позицию относительно начала часа
                              const topOffset = (startMinute / 60) * 24; // 24px на час
                              const top = topOffset;
                              
                              return (
                                <div
                                  key={lessonIndex}
                                  className={`lesson-event ${lesson.status || 'in_progress'} ${lesson.type === 'event' ? 'event' : ''}`}
                                  title={`${lesson.type === 'lesson' ? getStudentName(lesson.studentId, lesson.groupId) + ' - ' : ''}${lesson.title || 'Untitled Item'} (${formatTime(lesson.from || lesson.timeFrom || '9:00')}-${formatTime(lesson.to || lesson.timeTo || '10:00')})`}
                                  onClick={e => { e.stopPropagation(); handleEditLesson(lesson); }}
                                  style={{ 
                                    cursor: 'pointer',
                                    height: `${height}px`,
                                    top: `${top}px`,
                                    position: 'absolute'
                                  }}
                                >
                                  {getStudentNameWithTime(lesson)}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })
                    ) : (
                      (() => {
                        const weekData = getCalendarData()[0];
                        const dayIndex = (currentDate.getDay() + 6) % 7; // 0 = Monday, 6 = Sunday
                        const dayData = weekData[dayIndex];
                        const lessonsForThisTime = dayData.lessons.filter(lesson => {
                          if (!lesson.from || !lesson.to || lesson.allDay) return false;
                          
                          const fromTime = formatTime(lesson.from || lesson.timeFrom || '9:00');
                          const startHour = parseInt(fromTime.split(':')[0]);
                          
                          // Событие отображается только в ячейке начала
                          return slot.hour === startHour;
                        });
                        return (
                          <div 
                            key={timeIndex} 
                            className="time-cell"
                            onClick={() => {
                              if (dayData.date) {
                                const timeString = `${slot.hour.toString().padStart(2, '0')}:00`;
                                setModalTime(timeString);
                                setModalDate(dayData.date);
                                setModalOpen(true);
                              }
                            }}
                          >
                            {lessonsForThisTime.map((lesson, lessonIndex) => {
                              const fromTime = formatTime(lesson.from || lesson.timeFrom || '9:00');
                              const toTime = formatTime(lesson.to || lesson.timeTo || '10:00');
                              const startHour = parseInt(fromTime.split(':')[0]);
                              const startMinute = parseInt(fromTime.split(':')[1]) || 0;
                              const endHour = parseInt(toTime.split(':')[0]);
                              const endMinute = parseInt(toTime.split(':')[1]) || 0;
                              
                              const startTime = startHour + startMinute / 60;
                              const endTime = endHour + endMinute / 60;
                              const duration = endTime - startTime;
                              const height = Math.max(duration * 21, 21); // 28px на час
                              
                              // Вычисляем позицию относительно начала часа
                              const topOffset = (startMinute / 60) * 28; // 28px на час
                              const top = topOffset;
                              
                              return (
                                <div
                                  key={lessonIndex}
                                  className={`lesson-event ${lesson.status || 'in_progress'} ${lesson.type === 'event' ? 'event' : ''}`}
                                  title={`${lesson.type === 'lesson' ? getStudentName(lesson.studentId, lesson.groupId) + ' - ' : ''}${lesson.title || 'Untitled Item'} (${formatTime(lesson.from || lesson.timeFrom || '9:00')}-${formatTime(lesson.to || lesson.timeTo || '10:00')})`}
                                  onClick={e => { e.stopPropagation(); handleEditLesson(lesson); }}
                                  style={{ 
                                    cursor: 'pointer',
                                    height: `${height}px`,
                                    top: `${top}px`,
                                    position: 'absolute'
                                  }}
                                >
                                  {getStudentNameWithTime(lesson)}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()
                    )
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Selected Date Details Section */}
      {selectedDate && (
        <div className="selected-date-details" style={{ 
          marginTop: '30px', 
          padding: '25px', 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          zIndex: 1000
        }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
            Events for {selectedDate.toDateString()}
          </h3>
          <div className="day-lessons-list">
            {(() => {
              const lessonsForDate = getLessonsForDate(selectedDate)
                .sort((a, b) => {
                  // Сначала сортируем all-day события
                  if (a.allDay && !b.allDay) return -1;
                  if (!a.allDay && b.allDay) return 1;
                  
                  // Затем сортируем по времени начала
                  const timeA = a.from || '00:00';
                  const timeB = b.from || '00:00';
                  return timeA.localeCompare(timeB);
                });
              return lessonsForDate.map((lesson, index) => (
                <div key={index} className="lesson-item" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  borderLeft: '4px solid #333',
                  marginBottom: '10px',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onClick={() => handleEditLesson(lesson)}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateX(2px)';
                  e.target.style.background = '#f0f0f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateX(0)';
                  e.target.style.background = '#f8f9fa';
                }}
                >
                  <div className="lesson-time" style={{ fontWeight: '600', color: '#333', minWidth: '80px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {lesson.allDay ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="5"/>
                          <line x1="12" y1="1" x2="12" y2="3"/>
                          <line x1="12" y1="21" x2="12" y2="23"/>
                          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                          <line x1="1" y1="12" x2="3" y2="12"/>
                          <line x1="21" y1="12" x2="23" y2="12"/>
                          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                        </svg>
                        All Day
                      </>
                    ) : (
                      `${formatTime(lesson.from || lesson.timeFrom || '9:00')} - ${formatTime(lesson.to || lesson.timeTo || '10:00')}`
                    )}
                  </div>
                  <div className="lesson-info" style={{ flex: 1 }}>
                    <div className="lesson-title" style={{ fontWeight: '600', color: '#333', marginBottom: '4px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {lesson.type === 'event' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                      )}
                      {lesson.title || 'Untitled Item'}
                    </div>
                    {lesson.type === 'lesson' && (
                      <div className="lesson-student" style={{ color: '#666', fontSize: '14px' }}>
                        {getStudentName(lesson.studentId, lesson.groupId)}
                        <span style={{ color: '#888', fontSize: '0.9em', marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          {lesson.allDay ? (
                            <>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="5"/>
                                <line x1="12" y1="1" x2="12" y2="3"/>
                                <line x1="12" y1="21" x2="12" y2="23"/>
                                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                <line x1="1" y1="12" x2="3" y2="12"/>
                                <line x1="21" y1="12" x2="23" y2="12"/>
                                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                              </svg>
                              All Day
                            </>
                          ) : (
                            `${formatTime(lesson.from || lesson.timeFrom || '9:00')} - ${formatTime(lesson.to || lesson.timeTo || '10:00')}`
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={`lesson-status ${lesson.status || 'in_progress'}`} style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: lesson.status === 'done' ? '#d4edda' : lesson.status === 'cancelled' ? '#f8d7da' : '#ffeaa7',
                    color: lesson.status === 'done' ? '#155724' : lesson.status === 'cancelled' ? '#721c24' : '#856404'
                  }}>
                    {lesson.status || 'in progress'}
                  </div>
                </div>
              ));
            })()}
            {getLessonsForDate(selectedDate).length === 0 && (
              <p className="no-lessons" style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                No items scheduled for this date.
              </p>
            )}
          </div>
        </div>
      )}
      {!selectedDate && (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          marginTop: '30px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px dashed #ccc'
        }}>
          No date selected. Click on a day or the "+" indicator to see details.
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Schedule;
